/**
 * <yushe-shop>: the collection / search browser (snippets/yushe-shop.liquid).
 * - Any filter or sort change submits through Horizon's facets-form-component, which updates the URL
 *   and re-renders the section (Section Rendering API + morph), so the grid, chips and counts refresh
 *   without a reload. The element survives the morph, so listeners are delegated once.
 * - The "Hide sold out" switch in the bar is a proxy for the availability checkbox in the sheet.
 * - Grid density is client-only and remembered for the session.
 */
import { attachDragDismiss } from '@theme/yushe-drag-dismiss';

const DENSE_KEY = 'yushe-shop-dense';

class YusheShop extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound) return;
    this.dataset.bound = '1';

    const dense = sessionStorage.getItem(DENSE_KEY) === '1';
    this.setDense(dense);

    // In-stock sets lead, sold-out ones follow (v3). Shopify can't order by availability, so the loaded
    // cards are reordered on screen, again after every re-render or infinite-scroll append.
    this.leadInStock();

    // The filter sheet swipes back off to the right (it survives the facets morph, so delegate from here).
    attachDragDismiss(this, {
      panel: '.shop-sheet[open] .shop-sheet__panel',
      fade: (p, panel) => {
        const d = panel.closest('dialog');
        if (p === null) { delete d.dataset.dragging; d.style.removeProperty('--sheet-drag'); return; }
        d.dataset.dragging = '';
        d.style.setProperty('--sheet-drag', String(p));
      },
      dismiss: (panel) => panel.closest('dialog-component')?.closeDialog(),
    });
    this.mo = new MutationObserver(() => {
      clearTimeout(this.moTimer);
      this.moTimer = setTimeout(() => this.leadInStock(), 40);
    });
    this.mo.observe(this, { childList: true, subtree: true });

    this.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target.closest('[data-shop]') : null;
      if (!t) return;
      if (t.dataset.shop === 'dense-on') this.setDense(true);
      if (t.dataset.shop === 'dense-off') this.setDense(false);
    });

    this.addEventListener('change', (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
      if (el instanceof HTMLInputElement && el.dataset.shop === 'stock-proxy') {
        const real = this.querySelector('input[data-shop="stock"]');
        if (real instanceof HTMLInputElement) real.checked = el.checked;
      } else if (el instanceof HTMLInputElement && el.dataset.shop === 'stock') {
        const proxy = this.querySelector('input[data-shop="stock-proxy"]');
        if (proxy instanceof HTMLInputElement) proxy.checked = el.checked;
      }
      if (el.name || el.dataset.shop === 'stock-proxy') this.submit();
    });

    // Price fields: Enter applies without submitting the form natively.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const el = e.target;
      if (el instanceof HTMLInputElement && el.type === 'text' && el.name.startsWith('filter.v.price')) {
        e.preventDefault();
        this.submit();
      }
    });
  }

  disconnectedCallback() {
    if (this.mo) this.mo.disconnect();
  }

  submit() {
    const form = this.querySelector('facets-form-component');
    if (form && typeof form.updateFilters === 'function') form.updateFilters();
  }

  /**
   * Shows sold-out cards after the in-stock ones, keeping each group's own order.
   * Visual order only (CSS `order` on the grid cells): the DOM order must stay exactly as Shopify paginated
   * it, because Horizon's results-list reads the LAST card's data-page to decide which page to load next.
   * Moving page-1 cards to the end made it append page 2 again on every scroll (duplicate cards).
   */
  leadInStock() {
    const grid = this.querySelector('.shop-grid');
    if (!grid) return;
    const cells = [...grid.children].filter((el) => el.matches('.shop-grid__cell'));
    const soldOut = (el) => !!el.querySelector('.pcard[data-soldout], .pcard[data-available="false"], .pcard__flag');
    const visual = [...cells.filter((c) => !soldOut(c)), ...cells.filter(soldOut)];
    visual.forEach((c, i) => {
      const order = soldOut(c) ? '1' : '0';
      if (c.style.order !== order) c.style.order = order;
      c.style.setProperty('--i', String(Math.min(i, 8)));
    });
  }

  setDense(on) {
    this.dataset.dense = on ? 'true' : 'false';
    sessionStorage.setItem(DENSE_KEY, on ? '1' : '0');
    for (const b of this.querySelectorAll('[data-shop="dense-on"], [data-shop="dense-off"]')) {
      b.setAttribute('aria-pressed', String((b.dataset.shop === 'dense-on') === on));
    }
  }
}

if (!customElements.get('yushe-shop')) customElements.define('yushe-shop', YusheShop);

/** Search page field: show the clear button only while there is text. */
class YusheSearchField extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="search"]');
    const clear = this.querySelector('[data-clear]');
    if (!input || !clear) return;
    const sync = () => clear.toggleAttribute('hidden', !input.value);
    input.addEventListener('input', sync);
    clear.addEventListener('click', () => {
      input.value = '';
      sync();
      input.focus();
    });
    sync();
  }
}
if (!customElements.get('yushe-search-field')) customElements.define('yushe-search-field', YusheSearchField);
